import { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import URL from 'url-parse';
import type { Primitive } from 'utility-types';

import { call } from '@swf/core/api/embedded';
import type { ResolverTag } from '@swf/core/enums';
import type { Options } from '@swf/core/hooks/useResolver';
import useResolver from '@swf/core/hooks/useResolver';
import { select } from '@swf/core/store';

import { ScreenSizeContext } from '../providers/ScreenSizeProvider';

export type Query = Record<string, Primitive>;

interface Props {
  resolverTag: ResolverTag;
  resolverOptions?: Options;
  query?: Query;
  openTab?: boolean;
  centered?: boolean;
}

const getWindowFeatures = (
  width: number,
  height: number,
  top: number,
  left: number,
  customWindowWidth = 800,
  customWindowHeight = 650
): string => {
  const customWindowTop = top + height / 2 - customWindowHeight / 2;
  const customWindowLeft = left + width / 2 - customWindowWidth / 2;

  return (
    `titlebar=no,resizable=no,` +
    `width=${customWindowWidth},height=${customWindowHeight},left=${customWindowLeft},top=${customWindowTop}`
  );
};

const useOpenWindow = ({ query, openTab, resolverTag, centered, resolverOptions }: Props) => {
  const resolvedUrl = useResolver(resolverTag, resolverOptions);
  const locale = useSelector(select.locale.get);
  const embedded = useSelector(select.device.isEmbedded);
  const isMobile = useSelector(select.device.isMobile);
  const {
    windowSize: { width, height },
    windowPosition: { top, left }
  } = useContext(ScreenSizeContext);

  const windowFeatures =
    (centered && !isMobile && getWindowFeatures(width, height, top, left)) ||
    'scrollbars=yes,resizable=no';

  const openWindow = useCallback(() => {
    const parsed = new URL(resolvedUrl, true);
    parsed.query.language = locale;
    Object.assign(parsed.query, query);
    const options = openTab ? ['_blank'] : [resolverTag, windowFeatures];
    const url = parsed.toString();

    return embedded
      ? call(['app', 'execute'], `resolve/${resolverTag}`)
      : window.open(url, ...options);
  }, [resolvedUrl, locale, query, openTab, resolverTag, windowFeatures, embedded]);

  return openWindow;
};

export default useOpenWindow;
